import {
  useGetDemoQuery,
  useGetRecordingQuery,
  useGetRevisionsQuery,
  useUpdateDemoFromRecordingMutation,
  useUpdateDemoMutation,
} from '@carboncopyco/data';
import { UpdateOutlineIcon } from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Button,
  InlineLink,
  Loading,
  LoadingDots,
  TextBase,
  TextCaption,
  TextLargeParagraph,
  Tooltip,
  notify,
} from '@carboncopyco/ui';
import { MMMDoYYYY, dateUTCFromNow } from '@carboncopyco/utils';
import classNames from 'classnames';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePermissionsContext } from '../../../utils';
import { InfoPageEditableDescription } from '../../InfoPageEdiableDescription';
import { useDemoPageProps } from '../DemoPageContext';
import { useShouldUpdateDemoFromRecording } from '../useShouldUpdateDemoFromRecording';

export const DetailsCard = () => {
  const cloneLinkRef = useRef<HTMLDivElement>(null);
  const { organizationId, demoId, clonePath, workspaceId, readOnly } =
    useDemoPageProps();
  const { getPermissions } = usePermissionsContext();
  const [updateDemo] = useUpdateDemoMutation();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const { data: clone } = useGetRecordingQuery(
    {
      organizationId,
      id: String(demo?.recordId),
    },
    { skip: !demo || readOnly }
  );
  const { data } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'demo',
    id: demoId,
    reverseOrder: true,
  });
  const { description, creator, createTime = '' } = demo || {};
  const revisions = data?.items;
  const latestVersion = revisions?.[0];
  const defaultDescription = `Created by ${creator?.firstName} ${
    creator?.lastName
  }, on ${MMMDoYYYY(createTime)}`;

  const updateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );

  const updatesAvailable = useShouldUpdateDemoFromRecording(
    demoId,
    workspaceId,
    organizationId
  );
  const [updateDemoFromRecording, { isLoading: isLoadingUpdateFromRecording }] =
    useUpdateDemoFromRecordingMutation();

  return (
    <div className="flex flex-col grow border border-ds-natural-150 rounded-lg bg-ds-accent3-100">
      <div className="flex justify-between items-center px-6 h-ds-15 border-b border-b-ds-natural-150">
        <TextLargeParagraph
          color="text-ds-natural-700"
          label="Details"
        />
      </div>
      <div className="flex flex-col grow">
        {demo && revisions ? (
          <div className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-1">
              <TextCaption label="Description" />
              <div className="max-w-ds-96">
                <InfoPageEditableDescription
                  description={description || defaultDescription}
                  disabled={readOnly || !updateDemoPermission}
                  onDescriptionChange={(value) =>
                    value
                      ? updateDemo({
                          organizationId,
                          workspaceId,
                          id: demoId,
                          description: value,
                        })
                      : undefined
                  }
                />
              </div>
            </div>

            {!readOnly && (
              <div className="flex flex-col gap-1">
                <TextCaption label="Based on" />
                <div className="max-w-ds-96 hover:text-ds-primary">
                  <div className="flex items-center gap-1">
                    <TextBase
                      color="text-ds-natural-600"
                      label="Clone:"
                    />
                    <div
                      ref={cloneLinkRef}
                      className={classNames('truncate max-w-ds-96', {
                        'text-ds-natural-300': clone?.isDeleted,
                      })}
                    >
                      <InlineLink
                        disabled={demo?.readOnly || clone?.isDeleted}
                        as={Link}
                        to={clone?.isDeleted ? '' : clonePath}
                        label={`${clone?.recordName || ''}`}
                        truncateWidth={340}
                      />
                    </div>
                    {clone?.isDeleted && (
                      <Tooltip
                        trigger="mouseenter"
                        placement="top-start"
                        anchorRef={cloneLinkRef}
                        shadow="md"
                        noArrow
                      >
                        <div className="w-ds-52 p-3">
                          <TextCaption
                            label="The clone has been archived, so it is no longer available."
                            color="text-ds-natural-300"
                            lineHeight="leading-ds-50"
                          />
                        </div>
                      </Tooltip>
                    )}
                    {!demo.readOnly &&
                      updatesAvailable &&
                      updateDemoPermission && (
                        <Button
                          link
                          blue
                          leftIcon={
                            isLoadingUpdateFromRecording ? (
                              <Loading color="text-ds-primary-300" />
                            ) : (
                              <UpdateOutlineIcon />
                            )
                          }
                          label="Update"
                          onClick={() =>
                            updateDemoFromRecording({
                              organizationId,
                              id: demoId,
                            }).catch(() => notify.error('Error updating demo'))
                          }
                        />
                      )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <TextCaption label="Version" />
              {latestVersion && (
                <div className="flex items-center gap-1 max-w-ds-96">
                  <div className="whitespace-nowrap">
                    <TextBase
                      color="text-ds-natural-600"
                      label={`Version ${latestVersion.currRevision + 1}`}
                    />
                  </div>
                  <div className=" truncate text-ds-natural-300">
                    <TextCaption
                      label={`(Edited ${dateUTCFromNow(
                        latestVersion.createTime
                      )}, by ${latestVersion.creator.name})`}
                      ellipsis
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center grow">
            <LoadingDots />
          </div>
        )}
      </div>
    </div>
  );
};
